import userSelectors from '@elab/core/store/user/selectors';
import { constants, gwpLayoutStrategies } from '@elab/core/utils/constants';

const getCart = state => (state && state.cart) || {};
const getData = state => getCart(state).data || {};
const getUser = state => getData(state).user || {};
const getTaxes = state => getData(state).taxes || [];
const getTaxByCode = (state, code) => getTaxes(state).find(item => item.code === code) || {};
const getTaxAmount = (state, code) => getTaxByCode(state, code).appliedAmount || {};
const getGuid = state => getCart(state).guid;
const getEmail = state => getData(state).email;
const getCartType = state => getData(state).cartType || '';
const getUserName = state => getUser(state).name;
const getVoucherIsLoading = state => getCart(state).voucherIsLoading;
const getDeliveryAddress = state => getData(state).deliveryAddress || {};
const getPaymentAddress = state => getData(state).paymentAddress || {};
const getDeliverySlot = state => getData(state).deliverySlot || {};
const getDeliveryMode = state => getData(state).deliveryMode || {};
const getDeliveryModeCode = state => getDeliveryMode(state).code || '';
const getDeliveryModeIcon = state => getDeliveryMode(state).icon || {};
const getDeliveryModeIconUrl = state => getDeliveryModeIcon(state).url || '';
const getSupportedFeatures = state => getDeliveryMode(state).supportedFeatures || [];
const isDeliveryModePickup = state =>
    getDeliveryModeCode(state)
        .toLowerCase()
        .includes(constants.deliveryModeTypePickup);
const isDeliveryModeHome = state =>
    getDeliveryModeCode(state)
        .toLowerCase()
        .includes(constants.deliveryModeTypeHome);
const isDeliveryModeExpress = state =>
    getDeliveryModeCode(state)
        .toLowerCase()
        .includes(constants.deliveryModeTypeExpress);
const isDeliveryModeEmail = state =>
    getDeliveryModeCode(state)
        .toLowerCase()
        .includes(constants.deliveryModeTypeEmail);
const getCode = state => getCart(state).code;
const getHaveOutOfStock = state => getCart(state).haveOutOfStock;
const getAdjustedCartData = state => getCart(state).adjustedCartData || {};
const getAdjustedCartEntries = state => getAdjustedCartData(state).adjustedCartEntries || [];
const getAdjustedOutOfStockEntries = state =>
    getAdjustedCartData(state).adjustedOutOfStockEntries || [];
const getLimitativeCartEntries = state => getCart(state).limitativeBrands || [];
const isLimitativeBrandsReady = state => getCart(state).isLimitativeBrandsReady || false;
const getEntries = state => getData(state).entries || [];
const getCartGiftOptions = state => getData(state).giftOptions || [];
const getOrderInfos = state => getData(state).orderInfos || [];
const getHomeDeliveryDate = state => getCart(state).homeDeliveryDate || '';
const getCCExpressDeliveryDate = state => getCart(state).ccExpressDate || '';
const getCCTraditionalDeliveryDate = state => getCart(state).ccDate || '';
const getPostPointDeliveryDate = state => getCart(state).postPointDate || '';

const getPromotions = state => getData(state).appliedPromotions || [];
const getAppliedVouchers = state => getData(state).appliedVouchers || [];
const getAppliedPWPPromotion = state =>
    getPromotions(state).find(({ promotionType }) => promotionType === 'PWP');

const getEntriesByCategory = (state, category) =>
    getEntries(state).filter(({ product = {} }) => {
        const { categories = [] } = product;
        return categories.find(item => item.code === category);
    });

const getOutOfStockEntries = state =>
    getEntries(state).filter(
        ({ stockData, quantity }) => stockData && quantity > stockData.stockLevel,
    );

const getMinicartEntries = (state, productLayoutStrategy, gwpLayoutStrategy) => {
    // TECH-DEBT: BE didn't provide us information about related promotional products
    const entries = getEntriesWithoutFreeSamples(state).map((entry, i, arr) => ({
        ...entry,
        __originProduct: entry.giveAway && i > 0 ? arr[i - 1].product : null,
    }));

    if (productLayoutStrategy === 'LAST_ADDED_ON_TOP') {
        entries.reverse();
    }

    if (gwpLayoutStrategy) {
        const giftProducts = entries.filter(entry => entry.isGwp);
        const products = entries.filter(entry => !entry.isGwp);
        if (gwpLayoutStrategy === gwpLayoutStrategies.bottom) {
            return [...products, ...giftProducts];
        }
        giftProducts.map(giftEntry => {
            const index = products.findIndex(
                entry => entry.product.code === giftEntry.triggeredByProduct,
            );
            products.splice(
                gwpLayoutStrategy === gwpLayoutStrategies.below ? index + 1 : index,
                0,
                giftEntry,
            );
        });
        return products;
    }

    return entries;
};

const getEntriesWithoutFreeSamples = state => getEntries(state).filter(entry => !entry.freeSample);
const getFreeSampleEntries = state => getEntries(state).filter(entry => entry.freeSample);
const getGiftCardTransactions = state => getData(state).giftCardTransactions || [];
const getGiftCardTransactionByNumber = (state, number) =>
    getGiftCardTransactions(state).find(card => card.giftCard.cardNumber === number);
const getCouponDetails = state => getData(state).couponDetails || [];
const getAmountToPayValue = state => {
    const { amountToPay } = getData(state);
    return (amountToPay && amountToPay.value) || 0;
};
const getEntry = (state, productCode) =>
    getEntries(state).find(entry => entry.product.code === productCode);
const getEntryNumber = (state, productCode) => {
    const entry = getEntries(state).find(entry => entry.product.code === productCode);
    return entry && entry.entryNumber;
};
const getGWPEntries = state => getEntries(state).filter(el => el.giveAway);

const getTotalPrice = state => getData(state).totalPrice || {};
const getCurrencyIso = state => getTotalPrice(state).currencyIso;
const getHomeDeliveryOnly = state => getData(state).homeDeliveryOnly;

const isLoading = state => getCart(state).loading;
const isEmpty = state => getCart(state).isEmpty;
const getIsEmpty = state => isEmpty(state);
const isCheckoutBlocked = state => getData(state).checkoutBlocked;
const isPaymentAvailable = state => getData(state).paymentAvailable;
const isPlaceOrderBlocked = state => getData(state).placeOrderBlocked;
const isGuestCheckoutAllowed = state => getData(state).guestCheckoutAllowed;
const containsOtcProducts = state => !!getData(state).containsOtcProducts;
const getFirstEntry = state => getEntries(state)[0] || {};
const getNextPickupDate = state => getFirstEntry(state).nextPickupDate || '';
const getDeliveryPointOfService = state => getFirstEntry(state).deliveryPointOfService || {};

const hasLoadingCode = (state, code) => {
    const { loadingCodes } = getCart(state);
    return Array.isArray(loadingCodes) && loadingCodes.includes(code);
};
const isLoadingCode = (state, code) => isLoading(state) && hasLoadingCode(state, code);

const getMessages = state => getData(state).messages || [];
const getMessagesBySource = (state, source) =>
    getMessages(state).filter(message => message.source === source);
const getMessagesBySources = (state, source) => {
    if (Array.isArray(source)) {
        let messages = [];
        source.forEach(_source => {
            messages = [...messages, ...getMessagesBySource(state, _source)];
        });
        return messages;
    }
    return getMessagesBySource(state, source);
};

const getAnonymousAddress = state => getUser(state).guestAddress || {};

const isOnlyLoyaltyProducts = state => {
    const { redeemLoyaltyPoints, entries = [] } = getData(state);
    return redeemLoyaltyPoints && entries.every(({ product = {} }) => product.isLoyaltyProduct);
};

const getRedeemLoyaltyPoints = state => {
    return getData(state).redeemLoyaltyPoints || 0;
};

const getPayWithPointsAmount = state => {
    return getData(state).payWithPointsAmount || {};
};

const getCartId = state => {
    if (userSelectors.getUserId(state)) {
        return getCode(state);
    }
    return getGuid(state);
};

const isLoaded = state => getData(state).type;
const getMinicartCount = state => getCart(state).minicartCount;

const getForcedOutOfStock = state => getCart(state).forceOutOfStock;

const getStockLevelStatus = (state, code) => {
    const entry = getEntries(state).find(entry => entry.product.code === code);
    return entry ? entry.product.stock.stockLevelStatus : null;
};

const getStockLevelList = state => getCart(state).stockLevelList;
const getAllowedSamplesQuantity = state => getData(state).allowedFreeSamplesQuantity || 0;
const getFreeSamplesRefused = state => getData(state).freeSamplesRefused;
const getStockValidation = state => getCart(state).stockValidation;
const getHasLowStock = state => getCart(state).hasLowStock;
const getLowStockItems = state => getCart(state).lowStockItems;
const getGiftProducts = state => getEntries(state).filter(entry => entry.isGwp || entry.freeSample);
const getSkipAutoAdjust = state => getData(state).skipAutoAdjust || false;

const getEntriesWithoutFreeItems = state =>
    getEntriesWithoutFreeSamples(state).filter(entry => !entry.isGwp);

const getExpressPickupValidationMessage = state =>
    getData(state).messages
        ? getData(state).messages.filter(
              item => item.source === 'EXPRESS_PICKUP_VALIDATION' && item.type === 'WARN',
          )
        : [];
const getCartSummaryGiftOptions = state => {
    const giftOptionsTotal = getData(state).giftOptionsTotal || {};
    return giftOptionsTotal || {};
};

const getEGiftCardEntry = state =>
    getEntries(state).filter(entry =>
        entry.configurationInfos.find(info => info.configuratorType === 'EGIFTCARD'),
    )[0] || {};

const getEGiftCardConfigurationInfos = state => getEGiftCardEntry(state).configurationInfos || [];

const getUid = state => getUser(state).uid;
const isEgiftCardCartActive = state => getCart(state).egiftCardCartActive;
const getEGiftCardCode = state => getCart(state).egiftCardCode;
const getRegularCartId = state => getCart(state).regularCartId;

const getCheckLimitativeBrands = state => getCart(state).checkLimitativeBrands;
const hasOverlimitedBrands = state => getData(state).hasOverlimitedBrands || false;
const getOverlimitedBrands = state => getCart(state).overlimitedBrands || [];

const _getGwpItems = state => getCart(state).gwpItems;
const getGwpItems = state => _getGwpItems(state).gwpItems;
const getSelectedGwpItem = state => getCart(state).selectedGwpItem;

const getPhoneNumber = state => getData(state).phoneNumber || '';
const getPhoneNumberIsNotValid = state => !!getData(state).phoneNumberIsNotValid;
const getPhoneNumberSaved = state => !!getData(state).phoneNumberSaved;

const getTotalPriceWithTax = state => getData(state).totalPriceWithTax || {};
const getTotalPriceWithoutTax = state => getData(state).totalPriceWithoutVat || {};

const getTotalPriceWithoutDiscount = state => getData(state).totalPriceWithoutPromotions || {};
const getTotalDiscounts = state => getData(state).totalDiscounts || {};
const getTotalTax = state => getData(state).totalTax || {};
const getDeliveryCost = state => getData(state).deliveryCost || {};
const getTotalItems = state => getData(state).totalItems;
const getRewardLoyaltyPoints = state => getData(state).rewardLoyaltyPoints;
const getAppliedVouchersAmount = state => getData(state).appliedVouchersAmount || {};
const getPaymentMode = state => getData(state).paymentMode || {};
const getNoStock = (state, code) => {
    return !!getEntries(state).find(entry => {
        return entry.stockData && entry.stockData.stockLevelStatus === code;
    });
};

const getTemporaryAddress = state => getData(state).temporaryAddress || {};

const getAddToCartError = state => getCart(state).addToCardError;
const getRemoveFromCartError = state => getCart(state).removeFromCartError;

const getForbiddenEntriesByCountry = (state, country) => {
    return getEntries(state).filter(({ product }) => {
        const { forbiddenCountries } = product;
        return forbiddenCountries && forbiddenCountries.length
            ? forbiddenCountries.filter(({ isocode }) => isocode === country).length
            : false;
    });
};

const hasForbiddenEntriesInAdjustedCart = state => getAdjustedCartData(state).hasForbiddenStock;

const getPostPointStoreName = state => {
    const featuresEntry = (getDeliveryAddress(state).features || {}).entry || [];
    return (featuresEntry.find(data => data.key === 'posName') || {}).value;
};

const maxAllowedProductQuantity = state => getDeliveryMode(state).maxAllowedProductQuantity;

const entriesWithHigherQuantityThanAllowed = state => {
    const maxAllowed = maxAllowedProductQuantity(state);
    return maxAllowed ? getEntries(state).filter(({ quantity }) => quantity > maxAllowed) : [];
};

const getMaxAllowedProductExceeded = state => getCart(state).maxAllowedProductExceeded;

const getDeliveryMethodName = state => getDeliveryMode(state).name || '';

export default {
    getAmountToPayValue,
    getTaxAmount,
    getData,
    getGuid,
    getEmail,
    getDeliveryAddress,
    getPaymentAddress,
    getDeliveryMode,
    getDeliveryModeCode,
    getDeliveryModeIconUrl,
    isDeliveryModePickup,
    isDeliveryModeHome,
    isDeliveryModeExpress,
    isDeliveryModeEmail,
    getDeliverySlot,
    getTotalPrice,
    getCode,
    getCartId,
    isLoading,
    getEntries,
    getCartGiftOptions,
    getOrderInfos,
    getCurrencyIso,
    getEntry,
    isEmpty,
    getIsEmpty,
    isCheckoutBlocked,
    isPaymentAvailable,
    isPlaceOrderBlocked,
    isGuestCheckoutAllowed,
    getHomeDeliveryOnly,
    hasLoadingCode,
    isLoadingCode,
    getMessages,
    getMessagesBySource,
    getMessagesBySources,
    containsOtcProducts,
    isOnlyLoyaltyProducts,
    getNextPickupDate,
    getUserName,
    getDeliveryPointOfService,
    isLoaded,
    getAnonymousAddress,
    getGiftCardTransactions,
    getGiftCardTransactionByNumber,
    getCouponDetails,
    getCartType,
    getForcedOutOfStock,
    getMinicartEntries,
    getOutOfStockEntries,
    getStockLevelStatus,
    getEntryNumber,
    getStockLevelList,
    getSupportedFeatures,
    getEntriesByCategory,
    getAllowedSamplesQuantity,
    getFreeSamplesRefused,
    getStockValidation,
    getEntriesWithoutFreeSamples,
    getGWPEntries,
    getAppliedPWPPromotion,
    getHasLowStock,
    getLowStockItems,
    getHaveOutOfStock,
    getAdjustedCartEntries,
    getAdjustedOutOfStockEntries,
    getLimitativeCartEntries,
    getCheckLimitativeBrands,
    hasOverlimitedBrands,
    getOverlimitedBrands,
    isLimitativeBrandsReady,
    getGiftProducts,
    getEntriesWithoutFreeItems,
    getExpressPickupValidationMessage,
    getCartSummaryGiftOptions,
    getEGiftCardEntry,
    getEGiftCardConfigurationInfos,
    getUid,
    getMinicartCount,
    getFreeSampleEntries,
    getAdjustedCartData,
    getPromotions,
    getAppliedVouchers,
    getEGiftCardCode,
    isEgiftCardCartActive,
    getNoStock,
    getRegularCartId,
    getGwpItems,
    getSelectedGwpItem,
    getPhoneNumber,
    getPhoneNumberIsNotValid,
    getPhoneNumberSaved,
    getTotalPriceWithTax,
    getTotalDiscounts,
    getTotalTax,
    getVoucherIsLoading,
    getDeliveryCost,
    getSkipAutoAdjust,
    getRedeemLoyaltyPoints,
    getHomeDeliveryDate,
    getCCExpressDeliveryDate,
    getCCTraditionalDeliveryDate,
    getPostPointDeliveryDate,
    getTemporaryAddress,
    getAddToCartError,
    getRemoveFromCartError,
    getForbiddenEntriesByCountry,
    hasForbiddenEntriesInAdjustedCart,
    getPayWithPointsAmount,
    getTotalPriceWithoutDiscount,
    getTotalPriceWithoutTax,
    getPostPointStoreName,
    getTotalItems,
    getRewardLoyaltyPoints,
    getAppliedVouchersAmount,
    getPaymentMode,
    maxAllowedProductQuantity,
    entriesWithHigherQuantityThanAllowed,
    getMaxAllowedProductExceeded,
    getDeliveryMethodName,
};
